import React, { useCallback, useState } from 'react';
import { flushSync } from 'react-dom';

import { gql } from '@apollo/client';
import { useTranslation } from '@modernloop/i18n';
import { ZeroState } from '@modernloop/shared/components';
import { WarningIcon } from '@modernloop/shared/icons';
import { Box, Button, CircularProgress } from '@mui/material';
import { useSnackbar } from 'notistack';

import {
  SourceType,
  TaskPokeAgentMutation as TaskPokeAgentMutationResult,
  useScheduleTaskQuery,
  useTaskCancelMutation,
  useTaskPokeAgentMutation,
  useTaskSetIsUrgentMutation,
} from 'src/generated/mloop-graphql';

import { useFeatureFlagNumber } from 'src/hooks/feature-flag';
import { useActivityFeedRefetch } from 'src/hooks/useActivityFeedRefetch';
import useRefetchScheduleTaskQuery from 'src/hooks/useRefetchScheduleTaskQuery';

import getTaskUrl from 'src/urls/getTaskUrl';

import ScheduleTask from 'src/views-new/ScheduleTask';
import CancelTaskConfirmationDialog from 'src/views-new/ScheduleTask/CancelTaskConfirmationDialog';
import { ScheduleTaskMenuItemProps } from 'src/views-new/ScheduleTask/ScheduleTaskDetails';

import useScheduleTaskMenuOptions, { ScheduleTaskMenuOptions } from './useScheduleTaskMenuOptions';

type Props = {
  scheduleTaskId: string;
  showRequirements: boolean;
  onClose: (options?: { skipUnsavedNoteGuard?: boolean }) => void;
  onHasUnsavedNoteChangesChange: (value: boolean) => void;
  onNoteCreated: () => void;
};

/**
 * Owns schedule-task panel data loading and task actions without assuming which host shell renders it.
 */
export const useScheduleTaskPanelContentController = ({
  scheduleTaskId,
  showRequirements,
  onClose,
  onHasUnsavedNoteChangesChange,
  onNoteCreated,
}: Props) => {
  const { t } = useTranslation();
  const [showCancelScheduleTaskModal, setShowCancelScheduleTaskModal] = useState<boolean>(false);
  const refetchActivityFeed = useActivityFeedRefetch(SourceType.SourceTypeTask);
  const refetchScheduleTask = useRefetchScheduleTaskQuery();
  const taskPolling = useFeatureFlagNumber('user_task_polling_seconds');

  const { data, loading, error } = useScheduleTaskQuery({
    variables: { scheduleTaskId },
    fetchPolicy: 'cache-and-network',
    pollInterval: taskPolling ? taskPolling * 1000 : undefined,
  });
  const [taskSetIsUrgentMutation] = useTaskSetIsUrgentMutation();
  const [taskCancelMutation] = useTaskCancelMutation();
  const [taskPokeAgentMutation] = useTaskPokeAgentMutation();
  const { enqueueSnackbar } = useSnackbar();

  const showPokeAgentFailed = useCallback(() => {
    enqueueSnackbar(t('hiring-dashboard-tasks:tasks.pokeAgent.failed'), { variant: 'error' });
  }, [enqueueSnackbar, t]);

  const handlePokeAgentOutput = useCallback(
    (output: TaskPokeAgentMutationResult['taskPokeAgent'] | undefined) => {
      switch (output?.__typename) {
        case 'TaskPokeAgentSuccess':
          refetchActivityFeed();
          return;
        case 'TaskPokeAgentFailureNotDelegated':
        case 'UnknownError':
        case undefined:
          showPokeAgentFailed();
          return;
        default: {
          const exhaustiveOutput: never = output;
          return exhaustiveOutput;
        }
      }
    },
    [refetchActivityFeed, showPokeAgentFailed]
  );

  const menuOptions = useScheduleTaskMenuOptions({ task: data?.task || undefined }, undefined);
  const applicationId = data?.task?.application.id;
  const candidateId = data?.task?.application.candidate?.id;

  const handleMenuOptionSelected = useCallback(
    async (option: ScheduleTaskMenuItemProps) => {
      switch (option.id) {
        case ScheduleTaskMenuOptions.MarkAsUrgent: {
          await taskSetIsUrgentMutation({
            variables: { input: { taskId: scheduleTaskId, isUrgent: true } },
            optimisticResponse: { taskSetIsUrgent: { id: scheduleTaskId, isUrgent: true, __typename: 'Task' } },
          });
          refetchActivityFeed();
          break;
        }
        case ScheduleTaskMenuOptions.RemoveUrgent: {
          await taskSetIsUrgentMutation({
            variables: { input: { taskId: scheduleTaskId, isUrgent: false } },
            optimisticResponse: { taskSetIsUrgent: { id: scheduleTaskId, isUrgent: false, __typename: 'Task' } },
          });
          refetchActivityFeed();
          break;
        }
        case ScheduleTaskMenuOptions.CopyLinkToTask: {
          if (!applicationId || !candidateId) {
            return;
          }
          await navigator.clipboard.writeText(
            getTaskUrl(candidateId, scheduleTaskId, { applicationId, useRelativeUrl: false })
          );
          break;
        }
        case ScheduleTaskMenuOptions.PokeAgent: {
          try {
            const { data: pokeData } = await taskPokeAgentMutation({
              variables: { input: { taskId: scheduleTaskId } },
            });
            handlePokeAgentOutput(pokeData?.taskPokeAgent);
          } catch {
            // The customer-facing copy stays generic; the thrown error carries the backend detail.
            showPokeAgentFailed();
          }
          break;
        }
        case ScheduleTaskMenuOptions.CloseTask: {
          setShowCancelScheduleTaskModal(true);
          break;
        }
        default:
      }
    },
    [
      applicationId,
      candidateId,
      handlePokeAgentOutput,
      refetchActivityFeed,
      scheduleTaskId,
      showPokeAgentFailed,
      taskPokeAgentMutation,
      taskSetIsUrgentMutation,
    ]
  );

  const handleCancelTask = useCallback(async () => {
    await taskCancelMutation({ variables: { input: { taskId: scheduleTaskId } } });
    refetchScheduleTask();
    refetchActivityFeed();
    // The host close contract (see the "dismisses the cancel confirmation before calling the host
    // close callback" spec) requires the confirmation dialog to be out of the DOM before onClose
    // runs. React 18 batches this async flow, so force a synchronous commit here.
    flushSync(() => {
      setShowCancelScheduleTaskModal(false);
    });
    onHasUnsavedNoteChangesChange(false);
    onClose({ skipUnsavedNoteGuard: true });
  }, [
    onClose,
    onHasUnsavedNoteChangesChange,
    refetchActivityFeed,
    refetchScheduleTask,
    scheduleTaskId,
    taskCancelMutation,
  ]);

  const content = (
    <>
      {loading && !data?.task && (
        <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
          <CircularProgress size={32} />
        </Box>
      )}

      {error && (
        <Box padding={2.5}>
          <ZeroState
            label={t('hiring-dashboard-nav:nav.sidePanel.task.errorFetching')}
            icon={<WarningIcon color="error" sx={{ height: '32px', width: '32px' }} />}
          />
        </Box>
      )}

      {!error && !loading && !data?.task && (
        <Box padding={2.5}>
          <ZeroState
            label={t('hiring-dashboard-nav:nav.sidePanel.task.doesNotExist')}
            actions={<Button onClick={() => onClose()}>{t('hiring-dashboard-common:common.close')}</Button>}
          />
        </Box>
      )}

      {data?.task && (
        <ScheduleTask
          task={data.task}
          showRequirements={showRequirements}
          moreMenuOptions={menuOptions}
          onMenuOptionSelected={handleMenuOptionSelected}
          onHasUnsavedNoteChangesChange={onHasUnsavedNoteChangesChange}
          onNoteCreated={onNoteCreated}
        />
      )}

      {showCancelScheduleTaskModal && (
        <CancelTaskConfirmationDialog
          onClose={() => setShowCancelScheduleTaskModal(false)}
          onConfirm={handleCancelTask}
        />
      )}
    </>
  );

  return {
    content,
    menuOptions,
    handleMenuOptionSelected,
  };
};

const ScheduleTaskPanelContent = ({
  scheduleTaskId,
  showRequirements,
  onClose,
  onHasUnsavedNoteChangesChange,
  onNoteCreated,
}: Props) => {
  const { content } = useScheduleTaskPanelContentController({
    scheduleTaskId,
    showRequirements,
    onClose,
    onHasUnsavedNoteChangesChange,
    onNoteCreated,
  });
  return <>{content}</>;
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const TaskSetIsUrgentMutation = gql`
  mutation TaskSetIsUrgent($input: TaskSetIsUrgentInput!) {
    taskSetIsUrgent(input: $input) {
      id
      isUrgent
    }
  }
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const TaskCancelMutation = gql`
  mutation TaskCancel($input: TaskCancelInput!) {
    taskCancel(input: $input) {
      id
    }
  }
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const TaskPokeAgentMutation = gql`
  mutation TaskPokeAgent($input: TaskPokeAgentInput!) {
    taskPokeAgent(input: $input) {
      __typename
      ... on TaskPokeAgentSuccess {
        task {
          id
          delegateStatus
        }
      }
      ... on TaskPokeAgentFailureNotDelegated {
        errorCode
        errorMessage
      }
      ... on UnknownError {
        errorCode
        errorMessage
      }
    }
  }
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const ScheduleTaskQuery = gql`
  ${ScheduleTask.fragments.task}
  ${useScheduleTaskMenuOptions.fragments.task}
  query ScheduleTask($scheduleTaskId: uuid!) {
    task(id: $scheduleTaskId) {
      application {
        id
        candidate {
          id
        }
      }
      ...ScheduleTask_task
      ...UseSchduleTaskMenuOptions_task
    }
  }
`;

export default ScheduleTaskPanelContent;
